import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { X, Cookie, ChevronDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const STORAGE_KEY = "tde-cookie-consent";

const DEFAULT_PREFS = {
  necessary: true,
  functionality: false,
  experience: false,
  measurement: false,
  marketing: false,
  us_sale: false,
  us_sharing: false,
  us_targeted_ads: false,
};

// Expose a global to re-open the panel from the footer link
export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent("tde-open-cookie-preferences"));
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [showUS, setShowUS] = useState(false);
  const [showTracking, setShowTracking] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      setVisible(true);
    }
    const reopen = () => {
      // Load saved prefs when re-opening
      const s = localStorage.getItem(STORAGE_KEY);
      if (s) {
        try {
          setPrefs({ ...DEFAULT_PREFS, ...JSON.parse(s).prefs });
        } catch (e) {
          // ignore parse errors
        }
      }
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener("tde-open-cookie-preferences", reopen);
    return () => window.removeEventListener("tde-open-cookie-preferences", reopen);
  }, []);

  const save = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ts: new Date().toISOString(), prefs: choice })
    );
    setVisible(false);
    setExpanded(false);
  };

  const acceptAll = () => {
    const all = Object.fromEntries(Object.keys(DEFAULT_PREFS).map((k) => [k, true]));
    save(all);
  };
  const rejectAll = () => save({ ...DEFAULT_PREFS });
  const saveChoices = () => save(prefs);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-root"
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0f2340]/60 backdrop-blur-sm" />

      {/* Panel */}
      <div className="relative w-full md:max-w-2xl bg-white shadow-[0_20px_60px_-15px_rgba(15,35,64,0.5)] border-t md:border border-[#d4af37]/40 max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-6 px-6 md:px-10 pt-7 pb-4 border-b border-[#e2e8f0]">
          <div className="flex items-start gap-3">
            <div className="mt-1 flex-shrink-0 w-9 h-9 border border-[#d4af37]/60 flex items-center justify-center">
              <Cookie size={16} strokeWidth={1.6} className="text-[#d4af37]" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#d4af37] font-medium">
                {t("cookie.eyebrow")}
              </div>
              <h2 className="font-display text-2xl md:text-[26px] text-[#0f2340] leading-tight mt-1">
                {t("cookie.title")}
              </h2>
            </div>
          </div>
          {expanded && (
            <button
              data-testid="cookie-close-btn"
              onClick={() => setVisible(false)}
              className="text-[#475569] hover:text-[#0f2340] transition-colors mt-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 px-6 md:px-10 py-6 space-y-5">
          <p className="text-[14px] text-[#334155] leading-relaxed">
            {t("cookie.intro1")}
          </p>
          {expanded && (
            <>
              <p className="text-[13px] text-[#475569] leading-relaxed">
                {t("cookie.intro2")}
              </p>
              <p className="text-[13px] text-[#475569] leading-relaxed">
                {t("cookie.intro3")}
              </p>

              {/* US privacy rights section */}
              <Section
                title={t("cookie.usTitle")}
                description={t("cookie.usDesc")}
                open={showUS}
                onToggle={() => setShowUS(!showUS)}
              >
                <PrefRow
                  label={t("cookie.usSale")}
                  checked={prefs.us_sale}
                  onChange={(v) => setPrefs({ ...prefs, us_sale: v })}
                  testId="cookie-toggle-us-sale"
                />
                <PrefRow
                  label={t("cookie.usSharing")}
                  checked={prefs.us_sharing}
                  onChange={(v) => setPrefs({ ...prefs, us_sharing: v })}
                  testId="cookie-toggle-us-sharing"
                />
                <PrefRow
                  label={t("cookie.usTargeted")}
                  checked={prefs.us_targeted_ads}
                  onChange={(v) => setPrefs({ ...prefs, us_targeted_ads: v })}
                  testId="cookie-toggle-us-targeted"
                />
                <p className="text-[12px] text-[#64748b] leading-relaxed pt-2">
                  {t("cookie.usFooter")}{" "}
                  <Link to="/privacy" className="text-[#1a365d] underline decoration-[#d4af37] decoration-2 underline-offset-4 hover:text-[#d4af37]">
                    {t("cookie.privacyPolicy")}
                  </Link>
                  .
                </p>
              </Section>

              {/* Tracking technologies section */}
              <Section
                title={t("cookie.trackingTitle")}
                description={t("cookie.trackingDesc")}
                open={showTracking}
                onToggle={() => setShowTracking(!showTracking)}
              >
                <PrefRow
                  label={t("cookie.necessary")}
                  description={t("cookie.necessaryDesc")}
                  checked
                  disabled
                  testId="cookie-toggle-necessary"
                />
                <PrefRow
                  label={t("cookie.functionality")}
                  description={t("cookie.functionalityDesc")}
                  checked={prefs.functionality}
                  onChange={(v) => setPrefs({ ...prefs, functionality: v })}
                  testId="cookie-toggle-functionality"
                />
                <PrefRow
                  label={t("cookie.experience")}
                  description={t("cookie.experienceDesc")}
                  checked={prefs.experience}
                  onChange={(v) => setPrefs({ ...prefs, experience: v })}
                  testId="cookie-toggle-experience"
                />
                <PrefRow
                  label={t("cookie.measurement")}
                  description={t("cookie.measurementDesc")}
                  checked={prefs.measurement}
                  onChange={(v) => setPrefs({ ...prefs, measurement: v })}
                  testId="cookie-toggle-measurement"
                />
                <PrefRow
                  label={t("cookie.marketing")}
                  description={t("cookie.marketingDesc")}
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                  testId="cookie-toggle-marketing"
                />
              </Section>

              <p className="text-[11.5px] text-[#64748b] leading-relaxed pt-2">
                {t("cookie.readMore")}{" "}
                <Link to="/privacy" className="text-[#1a365d] underline decoration-[#d4af37] decoration-2 underline-offset-4 hover:text-[#d4af37]">
                  {t("cookie.privacyPolicy")}
                </Link>{" "}
                ·{" "}
                <Link to="/cookies" className="text-[#1a365d] underline decoration-[#d4af37] decoration-2 underline-offset-4 hover:text-[#d4af37]">
                  {t("cookie.cookiePolicy")}
                </Link>
              </p>
            </>
          )}
          {!expanded && (
            <div className="text-[12.5px] text-[#64748b]">
              {t("cookie.readMore")}{" "}
              <Link to="/privacy" className="text-[#1a365d] underline decoration-[#d4af37] decoration-2 underline-offset-4 hover:text-[#d4af37]">
                {t("cookie.privacyPolicy")}
              </Link>{" "}
              ·{" "}
              <Link to="/cookies" className="text-[#1a365d] underline decoration-[#d4af37] decoration-2 underline-offset-4 hover:text-[#d4af37]">
                {t("cookie.cookiePolicy")}
              </Link>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 md:px-10 py-5 border-t border-[#e2e8f0] bg-[#fafaf7] flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3">
          {!expanded ? (
            <button
              data-testid="cookie-customize-btn"
              onClick={() => setExpanded(true)}
              className="text-[11px] tracking-[0.24em] uppercase text-[#1a365d] hover:text-[#d4af37] transition-colors font-medium self-start md:self-center"
            >
              {t("cookie.customize")}
            </button>
          ) : (
            <button
              data-testid="cookie-save-btn"
              onClick={saveChoices}
              className="text-[11px] tracking-[0.24em] uppercase text-[#1a365d] hover:text-[#d4af37] transition-colors font-medium self-start md:self-center"
            >
              {t("cookie.savePrefs")}
            </button>
          )}
          <div className="flex flex-col md:flex-row gap-2 md:gap-3 w-full md:w-auto">
            <button
              data-testid="cookie-reject-all-btn"
              onClick={rejectAll}
              className="px-6 py-3 border border-[#0f2340] text-[#0f2340] text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-[#0f2340] hover:text-white transition-colors"
            >
              {t("cookie.rejectAll")}
            </button>
            <button
              data-testid="cookie-accept-all-btn"
              onClick={acceptAll}
              className="px-6 py-3 bg-[#d4af37] text-[#0f2340] text-[11px] tracking-[0.24em] uppercase font-semibold hover:bg-[#c19b2a] transition-colors shadow-[0_6px_18px_rgba(212,175,55,0.35)]"
            >
              {t("cookie.acceptAll")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, description, open, onToggle, children }) {
  return (
    <div className="border border-[#e2e8f0]">
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 px-4 py-4 text-left hover:bg-[#fafaf7] transition-colors"
      >
        <div>
          <div className="font-display text-[16px] text-[#0f2340] leading-snug">{title}</div>
          <p className="text-[12px] text-[#64748b] leading-relaxed mt-1">{description}</p>
        </div>
        <ChevronDown
          size={18}
          className={`text-[#1a365d] flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-4 pb-4 space-y-3 border-t border-[#e2e8f0] pt-4">{children}</div>}
    </div>
  );
}

function PrefRow({ label, description, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="flex-1 min-w-0">
        <div className={`font-body text-[13px] ${disabled ? "text-[#94a3b8]" : "text-[#0f2340]"} font-semibold`}>
          {label}
        </div>
        {description && (
          <p className="text-[11.5px] text-[#64748b] leading-relaxed mt-0.5">{description}</p>
        )}
      </div>
      <Switch
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        className="flex-shrink-0 mt-1 data-[state=checked]:bg-[#d4af37]"
      />
    </div>
  );
}
